import * as uuid from 'uuid';
import { difference, isEqual } from 'lodash-es';

import { ChangesStatus } from '@editor2/types/changesStatus';
import { type Conflict, type ReorderConflict, ConflictType } from '@editor2/types/conflict';
import { type CourseState } from '@editor2/types/courseState';
import { NgTraditional } from '@editor2/types/ngTraditional';
import { getModifiedDeletedConflict } from '../helpers/createConflictHelper';

export class ChapterReorderConflictDetector {
	detectConflicts = (state: CourseState, node) => {
		const initialValue = state.initialCourse.chapters.ids;
		const currentValue = state.chapters.ids;
		const incomingValue = node['chapters'];

		let updatedInitial = [...initialValue];
		let updatedCurrent = [...currentValue];

		const conflicts: (Conflict | ReorderConflict)[] = [];
		const conflictedItems = [];

		// if incoming chapter deleted and current is modified
		const incomingDeletedChapters = difference(initialValue, incomingValue);

		incomingDeletedChapters.map((id: string) => {
			if (
				state.changes.chapters[id]?.includes(ChangesStatus.Modified) ||
				state.changes.chapters[id]?.includes(ChangesStatus.ChildrenReorder)
			) {
				const conflict = getModifiedDeletedConflict({
					attribute: 'chapters',
					id,
					state,
					entityId: id,
					entityType: NgTraditional.Chapter
				});
				conflicts.push(conflict);
				conflictedItems.push(id);
			}
		});

		switch (true) {
			case isEqual(currentValue, incomingValue): {
				updatedInitial = [...incomingValue];
				updatedCurrent = [...incomingValue];
				break;
			}

			case isEqual(initialValue, currentValue) && !isEqual(initialValue, incomingValue): {
				// no conflict, just apply the changes both to initial and current
				updatedInitial = [...incomingValue, ...conflictedItems];
				updatedCurrent = [...incomingValue, ...conflictedItems];
				break;
			}

			case !isEqual(initialValue, currentValue) && !isEqual(initialValue, incomingValue): {
				// check if position conflict present

				const isEqualLength =
					initialValue.length === currentValue.length &&
					initialValue.length === incomingValue.length;
				const positionConflictPresent = initialValue.some(
					(id, i) => initialValue[i] !== currentValue[i] && initialValue[i] !== incomingValue[i]
				);

				if (positionConflictPresent || !isEqualLength) {
					const conflict: ReorderConflict = {
						conflictId: uuid.v4(),
						conflictType: ConflictType.ChildrenReorder,
						entityId: '',
						entityType: NgTraditional.Book,
						attribute: 'chapters',
						initial: initialValue,
						current: currentValue,
						incoming: incomingValue
					};
					conflicts.push(conflict);
				} else {
					const merged = initialValue.map((id, i) => {
						if (initialValue[i] === currentValue[i]) return incomingValue[i];
						else if (initialValue[i] === incomingValue[i]) return currentValue[i];
					});

					updatedInitial = [...incomingValue];
					updatedCurrent = [...merged];
				}

				break;
			}

			default:
				break;
		}

		return { conflicts, updatedInitial, updatedCurrent };
	};

	applyDiffToInitial = ({ state, updatedInitial }) => {
		state.initialCourse.chapters.ids = [...updatedInitial];
	};

	applyDiffToCurrent = ({ state, updatedCurrent }) => {
		state.chapters.ids = [...updatedCurrent];
	};

	applyConflict = ({ state, conflicts }) => {
		state.conflicts = [...state.conflicts, ...conflicts];
	};
}
